import { useEffect, useMemo, useState } from 'react';
import { getFormations } from '../services/formationService';
import {
  getFormationDocuments,
  createFormationDocument,
  updateFormationDocument,
  deleteFormationDocument,
  analyserImportDocuments,
  enregistrerImportDocuments,
} from '../services/formationDocumentService';
import SelectRecherche from '../components/SelectRecherche';
import ImportTexteModal from '../components/ImportTexteModal';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/Parametrage.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';

/** Une ligne de la création : un document, son ordre, sa précision. */
const LIGNE_VIDE = { libelle: '', ordre: '', description: '' };

const DOCUMENT_VIDE = {
  formationId: '',
  // À la création seulement : plusieurs documents, pour plusieurs formations.
  formationIds: [],
  lignes: [{ ...LIGNE_VIDE }],
  libelle: '',
  description: '',
  obligatoire: true,
  ordre: '',
  actif: true,
};

function documentToForm(doc) {
  return {
    formationId: doc.formationId ?? '',
    libelle: doc.libelle ?? '',
    description: doc.description ?? '',
    obligatoire: doc.obligatoire ?? true,
    ordre: doc.ordre ?? '',
    actif: doc.actif ?? true,
  };
}

/**
 * Paramétrage > Documents à apporter : les pièces que le client doit
 * fournir, formation par formation. Elles sont listées dans l'email de devis
 * ({{documents}}), proposées au dépôt sur la page de signature, et signalées
 * comme manquantes dans le dossier client. Même gabarit que les rappels.
 */
function ParametrageDocuments() {
  const [formations, setFormations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // '' = toutes les formations ; sinon l'id de celle qu'on regarde.
  const [filtreFormation, setFiltreFormation] = useState('');

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(DOCUMENT_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [formError, setFormError] = useState(null);

  const [suppression, setSuppression] = useState(null);
  const [flash, setFlash] = useState(null);
  // Import par copier-coller (cf. ImportTexteModal, comme pour les formations).
  const [importOuvert, setImportOuvert] = useState(false);

  function chargerDocuments() {
    return getFormationDocuments()
      .then((res) => setDocuments(res.data || []))
      .catch(() => setError('Impossible de charger les documents à apporter.'));
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getFormations().then((res) => setFormations(res.data || [])),
      chargerDocuments(),
    ])
      .catch(() => setError('Impossible de charger le catalogue.'))
      .finally(() => setLoading(false));
  }, []);

  // Formations actives d'abord, dans l'ordre du catalogue ; une formation
  // retirée qui garde des documents reste visible, pour les nettoyer.
  const formationsAffichees = useMemo(() => {
    const avecDocs = new Set(documents.map((d) => d.formationId));
    return formations
      .filter((f) => f.actif || avecDocs.has(f.id))
      .filter((f) => !filtreFormation || String(f.id) === String(filtreFormation));
  }, [formations, documents, filtreFormation]);

  function ouvrirCreation(formationId) {
    setEdition({ id: null });
    const initiale = formationId ?? filtreFormation ?? '';
    setForm({
      ...DOCUMENT_VIDE,
      lignes: [{ ...LIGNE_VIDE }],
      formationId: initiale,
      formationIds: initiale ? [String(initiale)] : [],
    });
    setFormError(null);
  }

  function ouvrirEdition(doc) {
    setEdition({ id: doc.id });
    setForm(documentToForm(doc));
    setFormError(null);
  }

  function fermerModale() {
    setEdition(null);
    setFormError(null);
  }

  /** Les lignes de la création : modifier, ajouter, retirer. */
  function changerLigne(index, champ, valeur) {
    setForm((prev) => ({
      ...prev,
      lignes: prev.lignes.map((l, i) => (i === index ? { ...l, [champ]: valeur } : l)),
    }));
  }
  function ajouterLigne() {
    setForm((prev) => ({ ...prev, lignes: [...prev.lignes, { ...LIGNE_VIDE }] }));
  }
  function retirerLigne(index) {
    setForm((prev) => ({
      ...prev,
      lignes: prev.lignes.length > 1 ? prev.lignes.filter((_, i) => i !== index) : prev.lignes,
    }));
  }

  function handleFieldChange(champ) {
    return (e) => {
      const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [champ]: valeur }));
    };
  }

  /**
   * Reporte les valeurs lues par l'IA. En édition, ce sont les champs de la
   * fiche. En création, le document va dans la première ligne vide (ou une
   * nouvelle) et la formation s'ajoute à celles déjà cochées.
   */
  function appliquerTexte(v) {
    setForm((prev) => {
      const { formationId, ...doc } = v;
      const suite = { ...prev };
      if (doc.ordre !== undefined) doc.ordre = String(doc.ordre);
      if (formationId !== undefined) {
        const id = String(formationId);
        suite.formationId = id;
        if (edition?.id === null && !prev.formationIds.map(String).includes(id)) {
          suite.formationIds = [...prev.formationIds, id];
        }
      }
      if (edition?.id !== null) return { ...suite, ...doc };
      if (Object.keys(doc).length === 0) return suite;
      const vide = prev.lignes.findIndex((l) => !l.libelle.trim());
      suite.lignes =
        vide >= 0
          ? prev.lignes.map((l, i) => (i === vide ? { ...l, ...doc } : l))
          : [...prev.lignes, { ...LIGNE_VIDE, ...doc }];
      return suite;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const { formationIds, lignes, ...reste } = form;
    const payload = {
      ...reste,
      formationId: Number(form.formationId),
      ordre: form.ordre === '' ? 0 : Number(form.ordre),
    };

    try {
      if (edition.id === null) {
        const documents = lignes.filter((l) => l.libelle.trim());
        if (formationIds.length === 0) {
          setFormError('Choisissez au moins une formation.');
          setSaving(false);
          return;
        }
        if (documents.length === 0) {
          setFormError('Renseignez au moins un document.');
          setSaving(false);
          return;
        }
        // Chaque document, pour chaque formation cochée : autant de lignes,
        // chacune modifiable et supprimable à part ensuite.
        let crees = 0;
        for (const id of formationIds) {
          for (const doc of documents) {
            await createFormationDocument({
              ...payload,
              formationId: Number(id),
              libelle: doc.libelle.trim(),
              description: doc.description,
              ordre: doc.ordre === '' ? 0 : Number(doc.ordre),
            });
            crees += 1;
          }
        }
        setFlash(
          crees > 1
            ? `${crees} documents ajoutés (${documents.length} × ${formationIds.length} formation${formationIds.length > 1 ? 's' : ''}).`
            : 'Document ajouté.'
        );
      } else {
        await updateFormationDocument(edition.id, payload);
        setFlash('Document mis à jour.');
      }
      await chargerDocuments();
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteFormationDocument(suppression.id);
      await chargerDocuments();
      setFlash('Document supprimé.');
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
    } finally {
      setSuppression(null);
      setSaving(false);
    }
  }

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Documents à apporter</h1>
          <p className="param-subtitle">
            Pièces à fournir par formation, listées dans l’email de devis.
          </p>
        </div>
        <div className="param-header-actions">
          <button
            type="button"
            className="param-btn"
            onClick={() => setImportOuvert(true)}
            title="Coller une liste de pièces à fournir : l'IA la lit, vous relisez avant d'enregistrer"
          >
            Importer depuis un texte
          </button>
          <button type="button" className="param-btn param-btn-primary" onClick={() => ouvrirCreation()}>
            + Ajouter un document
          </button>
        </div>
      </div>

      <div className="param-toolbar">
        <div className="param-filtre">
          <SelectRecherche
            className="param-filtre-select"
            value={filtreFormation}
            onChange={(e) => setFiltreFormation(e.target.value)}
          >
            <option value="">Toutes les formations</option>
            {formations
              .filter((f) => f.actif)
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code} — {f.intitule}
                </option>
              ))}
          </SelectRecherche>
          {filtreFormation && (
            <button
              type="button"
              className="param-filtre-clear"
              onClick={() => setFiltreFormation('')}
            >
              Toutes
            </button>
          )}
        </div>
        {flash && <span className="param-flash">{flash}</span>}
      </div>

      <div className="param-scroll">
        {loading && (
          <div className="param-card">
            <div className="param-state">Chargement...</div>
          </div>
        )}

        {!loading && error && (
          <div className="param-card">
            <div className="param-state param-state-error">{error}</div>
          </div>
        )}

        {!loading &&
          !error &&
          formationsAffichees.map((formation) => {
            const docs = documents.filter((d) => d.formationId === formation.id);
            return (
              <div className="param-card" key={formation.id}>
                <div className="param-card-header">
                  <div>
                    <div className="param-section-titre">
                      {formation.intitule}
                      <span className="param-compteur">{docs.length}</span>
                    </div>
                    <div className="param-card-note">
                      {formation.code}
                      {!formation.actif ? ' · formation retirée du catalogue' : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="param-btn param-btn-sm"
                    onClick={() => ouvrirCreation(formation.id)}
                  >
                    + Ajouter
                  </button>
                </div>

                {docs.length === 0 ? (
                  <div className="param-empty">
                    <div className="param-empty-title">Aucun document demandé.</div>
                    <div className="param-empty-subtitle">
                      L&apos;email de devis de cette formation ne mentionnera rien à apporter.
                    </div>
                  </div>
                ) : (
                  <div className="param-table-wrap">
                    <table className="param-table">
                      <thead>
                        <tr>
                          <th>DOCUMENT</th>
                          <th className="param-th-etat">EXIGENCE</th>
                          <th className="param-th-etat">ÉTAT</th>
                          <th className="param-th-actions">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map((doc) => (
                          <tr key={doc.id}>
                            <td>
                              <div className="param-intitule">{doc.libelle}</div>
                              {doc.description && (
                                <div className="param-description">{doc.description}</div>
                              )}
                            </td>
                            <td>
                              <span className={`param-pill${doc.obligatoire ? '' : ' param-pill-muted'}`}>
                                {doc.obligatoire ? 'Obligatoire' : 'Facultatif'}
                              </span>
                            </td>
                            <td>
                              <span className={`param-pill${doc.actif ? '' : ' param-pill-muted'}`}>
                                {doc.actif ? 'Demandé' : 'Suspendu'}
                              </span>
                            </td>
                            <td>
                              <div className="param-row-actions">
                                <button
                                  type="button"
                                  className="param-btn param-btn-sm"
                                  onClick={() => ouvrirEdition(doc)}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="param-btn param-btn-sm param-btn-danger"
                                  onClick={() => setSuppression(doc)}
                                >
                                  Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {edition && (
        <div className="param-modal-overlay" onClick={fermerModale}>
          <div className="param-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">
                {edition.id === null ? 'Nouveau document à apporter' : 'Modifier le document'}
              </h2>
              <button type="button" className="param-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && (
                <div className="param-form-message param-form-message-error">{formError}</div>
              )}

              {/* Création comme modification ; la case Obligatoire / Demandé reste à cocher à la main. */}
              <RemplirParTexte
                variante="param"
                contexte="document à apporter par le client pour une formation (pièce à fournir)"
                champs={[
                  {
                    cle: 'formationId',
                    libelle: edition.id === null ? 'Formations' : 'Formation',
                    type: 'choix',
                    options: formations
                      .filter((f) => f.actif || String(f.id) === String(form.formationId))
                      .map((f) => ({ valeur: String(f.id), libelle: `${f.code} — ${f.intitule}` })),
                  },
                  { cle: 'libelle', libelle: 'Document' },
                  { cle: 'description', libelle: 'Précision pour le client' },
                  { cle: 'ordre', libelle: 'Ordre', type: 'nombre' },
                ]}
                onValeurs={appliquerTexte}
              />

              <label className="param-field">
                <span className="param-label">
                  {edition.id === null ? 'Formations' : 'Formation'}
                </span>
                {edition.id === null ? (
                  // Création : autant de formations qu'on veut, un document
                  // sera créé pour chacune.
                  <SelectRecherche
                    className="param-select"
                    multiple
                    value={form.formationIds}
                    onChange={(e) => setForm((prev) => ({ ...prev, formationIds: e.target.value }))}
                    placeholder="Choisir une ou plusieurs formations…"
                    required
                  >
                    {formations
                      .filter((f) => f.actif)
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.code} — {f.intitule}
                        </option>
                      ))}
                  </SelectRecherche>
                ) : (
                  <SelectRecherche
                    className="param-select"
                    value={form.formationId}
                    onChange={handleFieldChange('formationId')}
                    required
                  >
                    <option value="">Choisir une formation…</option>
                    {formations
                      .filter((f) => f.actif || String(f.id) === String(form.formationId))
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.code} — {f.intitule}
                        </option>
                      ))}
                  </SelectRecherche>
                )}
              </label>

              {edition.id === null ? (
                <div className="param-field">
                  <span className="param-label">Documents</span>
                  <div className="documents-lignes">
                    {form.lignes.map((ligne, i) => (
                      <div className="documents-ligne" key={i}>
                        <div className="param-form-row">
                          <label className="param-field param-field-grow">
                            <span className="param-label">Document {i + 1}</span>
                            <input
                              type="text"
                              className="param-input"
                              maxLength={150}
                              placeholder="Ex. : procès-verbal des élections du CSE"
                              value={ligne.libelle}
                              onChange={(e) => changerLigne(i, 'libelle', e.target.value)}
                              required={i === 0}
                            />
                          </label>
                          <label className="param-field documents-ligne-ordre">
                            <span className="param-label">Ordre</span>
                            <input
                              type="number"
                              min="0"
                              max="999"
                              className="param-input"
                              value={ligne.ordre}
                              onChange={(e) => changerLigne(i, 'ordre', e.target.value)}
                            />
                          </label>
                          <button
                            type="button"
                            className="param-btn param-btn-sm documents-ligne-retirer"
                            onClick={() => retirerLigne(i)}
                            disabled={form.lignes.length === 1}
                            aria-label={`Retirer le document ${i + 1}`}
                            title="Retirer"
                          >
                            ×
                          </button>
                        </div>
                        <input
                          type="text"
                          className="param-input"
                          placeholder="Précision pour le client (facultatif) : ce qu'il faut fournir, sous quelle forme…"
                          value={ligne.description}
                          onChange={(e) => changerLigne(i, 'description', e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <button type="button" className="param-btn param-btn-sm" onClick={ajouterLigne}>
                    + Ajouter un document
                  </button>
                </div>
              ) : (
                <>
                  <div className="param-form-row">
                    <label className="param-field param-field-grow">
                      <span className="param-label">Document</span>
                      <input
                        type="text"
                        className="param-input"
                        maxLength={150}
                        placeholder="Ex. : procès-verbal des élections du CSE"
                        value={form.libelle}
                        onChange={handleFieldChange('libelle')}
                        required
                      />
                    </label>
                    <label className="param-field">
                      <span className="param-label">Ordre</span>
                      <input
                        type="number"
                        min="0"
                        max="999"
                        className="param-input"
                        value={form.ordre}
                        onChange={handleFieldChange('ordre')}
                      />
                    </label>
                  </div>

                  <label className="param-field">
                    <span className="param-label">Précision pour le client</span>
                    <textarea
                      className="param-textarea"
                      rows={3}
                      placeholder="Ce qu'il faut fournir exactement, sous quelle forme…"
                      value={form.description}
                      onChange={handleFieldChange('description')}
                    />
                  </label>
                </>
              )}

              <label className="param-checkbox-field">
                <input
                  type="checkbox"
                  checked={form.obligatoire}
                  onChange={handleFieldChange('obligatoire')}
                />
                <span>Obligatoire (signalé comme manquant tant qu&apos;il n&apos;est pas déposé)</span>
              </label>

              <label className="param-checkbox-field">
                <input type="checkbox" checked={form.actif} onChange={handleFieldChange('actif')} />
                <span>Demandé (figure dans l&apos;email et sur la page de signature)</span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement du document" estimation />
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Ajouter' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer le document</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Ne plus demander <strong>{suppression.libelle}</strong> pour{' '}
                {suppression.formationIntitule} ? Les fichiers déjà reçus restent dans les
                dossiers clients.
              </p>
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importOuvert && (
        <ImportTexteModal
          titre="Importer des documents à apporter depuis un texte"
          aide="Collez la liste des pièces à fournir par formation. Une pièce déjà demandée est mise à jour, pas dupliquée."
          exemple={
            'Exemple :\nFormation SSCT (moins de 50 salariés) :\n- Procès-verbal des élections du CSE (obligatoire)\n- Liste des élus avec leurs mandats\nFormation économique :\n- Derniers comptes annuels de l’entreprise, si possible\n…'
          }
          motPluriel="documents"
          cleObjet="document"
          colonnes={[
            {
              cle: 'formationId',
              label: 'Formation',
              type: 'select',
              numerique: true,
              obligatoire: true,
              vide: '— Choisir —',
              largeur: 240,
              options: formations.map((f) => ({ value: f.id, label: `${f.code} — ${f.intitule}` })),
            },
            { cle: 'libelle', label: 'Document', obligatoire: true, largeur: 240 },
            { cle: 'description', label: 'Précision', type: 'textarea', largeur: 240 },
            { cle: 'obligatoire', label: 'Oblig.', type: 'checkbox' },
            { cle: 'ordre', label: 'Ordre', type: 'number', pas: '1', min: 0, largeur: 70 },
          ]}
          analyser={(texte) => analyserImportDocuments(texte).then((res) => res.data)}
          enregistrer={(retenues) =>
            enregistrerImportDocuments(
              retenues.map((p) => ({ existantId: p.existantId, document: p.document }))
            ).then((res) => res.data)
          }
          onFermer={() => setImportOuvert(false)}
          onEnregistre={(bilan) => {
            if (bilan.creees > 0 || bilan.misesAJour > 0) {
              setFlash(
                `${bilan.creees} document${bilan.creees > 1 ? 's' : ''} créé${bilan.creees > 1 ? 's' : ''}, ${bilan.misesAJour} mis à jour depuis le texte.`
              );
              chargerDocuments();
            }
          }}
        />
      )}
    </div>
  );
}

export default ParametrageDocuments;
